/**
 * Daily notes on Today (Liam, 26 Oct): a lined scratchpad under the Agenda. Nothing is saved in ONE — the text lives in
 * this browser only, and is wiped at midnight once it has sat untouched for 7 days. "Make a task" turns the current line
 * (or the selection) into a task through quick-add; "Keep as note" saves the whole pad as a note titled with today's date.
 */
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CheckSquare, FilePlus, NotebookPen } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { post } from "../lib/api";
import { Card, ErrorNote } from "./ui";

const KEY = "one.daily-notes";
const KEEP_DAYS = 7;
interface Saved { text: string; usedAt: string }

const load = (): Saved | null => { try { const v = localStorage.getItem(KEY); return v ? (JSON.parse(v) as Saved) : null; } catch { return null; } };
const save = (s: Saved | null) => { try { if (s) localStorage.setItem(KEY, JSON.stringify(s)); else localStorage.removeItem(KEY); } catch { /* private mode: the pad just won't persist */ } };
/** Midnight after the pad has been untouched for KEEP_DAYS days (Malta time). */
const expired = (usedAt: string) => {
  const last = new Date(usedAt);
  const cutoff = new Date(last.getFullYear(), last.getMonth(), last.getDate() + KEEP_DAYS); // local midnight
  return Date.now() >= cutoff.getTime();
};
const today = () => new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

export function DailyNotes() {
  const qc = useQueryClient();
  const ref = useRef<HTMLTextAreaElement>(null);
  const [text, setText] = useState(() => { const s = load(); return s && !expired(s.usedAt) ? s.text : ""; });
  const [busy, setBusy] = useState<"task" | "note" | null>(null);
  const [flash, setFlash] = useState<React.ReactNode>(null);
  const [error, setError] = useState<unknown>(null);

  // one check a minute: the wipe happens at midnight, not only on the next reload
  useEffect(() => {
    const t = setInterval(() => { const s = load(); if (s && expired(s.usedAt)) { save(null); setText(""); } }, 60_000);
    return () => clearInterval(t);
  }, []);
  const change = (v: string) => { setText(v); save(v.trim() ? { text: v, usedAt: new Date().toISOString() } : null); };
  const touch = () => { if (text.trim()) save({ text, usedAt: new Date().toISOString() }); };

  /** The selected text, else the line the cursor is on. */
  const currentLine = () => {
    const el = ref.current;
    if (!el) return "";
    const { selectionStart: a, selectionEnd: b } = el;
    if (a !== b) return text.slice(a, b).trim();
    const start = text.lastIndexOf("\n", a - 1) + 1;
    const end = text.indexOf("\n", a);
    return text.slice(start, end < 0 ? text.length : end).trim();
  };
  const makeTask = async () => {
    const line = currentLine();
    if (!line) { setError(new Error("Put the cursor on the line to turn into a task, or select the text.")); return; }
    setBusy("task"); setError(null);
    try {
      const r = await post<{ item: { id: string; ref: string; title: string } }>("/api/tasks/quick", { text: line });
      touch();
      setFlash(<>Task added: <Link to="/work/$id" params={{ id: r.item.id }} className="text-brand-700 hover:underline">{r.item.ref} {r.item.title}</Link></>);
      for (const k of ["my-day", "plan-today", "triage", "plan-sort-count"]) void qc.invalidateQueries({ queryKey: [k] });
    } catch (e) { setError(e); } finally { setBusy(null); }
  };
  const keepNote = async () => {
    if (!text.trim()) return;
    setBusy("note"); setError(null);
    try {
      const r = await post<{ id: string }>("/api/notes", { title: `Daily notes — ${today()}`, body: text.trim() });
      touch();
      setFlash(<>Kept as <a href={`/notes?note=${r.id}`} className="text-brand-700 hover:underline">a note</a>.</>);
      void qc.invalidateQueries({ queryKey: ["notes"] });
    } catch (e) { setError(e); } finally { setBusy(null); }
  };

  return (
    <Card title="Daily notes" icon={<NotebookPen size={16} />} actions={text.trim() ? <button type="button" className="btn btn-ghost btn-sm text-xs" onClick={() => { change(""); setFlash(null); }}>Clear</button> : null}>
      <p className="muted -mt-1 mb-2 text-xs">Scribble as the day goes. Kept on this device only; wiped at midnight after 7 days untouched.</p>
      <textarea
        ref={ref}
        className="w-full resize-y rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 text-sm leading-7 outline-none focus:border-brand-400"
        style={{ minHeight: "12.25rem", backgroundImage: "repeating-linear-gradient(to bottom, transparent 0, transparent calc(1.75rem - 1px), var(--line) calc(1.75rem - 1px), var(--line) 1.75rem)", backgroundAttachment: "local" }}
        placeholder="Call Aaron re jacuzzi height…"
        value={text}
        onChange={(e) => change(e.target.value)}
        aria-label="Daily notes"
      />
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {flash ? <span className="muted min-w-0 flex-1 truncate text-xs">{flash}</span> : <span className="flex-1" />}
        <button type="button" className="btn btn-sm" disabled={!text.trim() || busy !== null} onClick={() => void makeTask()} title="The line under the cursor, or the selected text, becomes a task"><CheckSquare size={13} /> Make a task</button>
        <button type="button" className="btn btn-sm" disabled={!text.trim() || busy !== null} onClick={() => void keepNote()} title="Save the whole pad as a note titled with today's date"><FilePlus size={13} /> {busy === "note" ? "Keeping…" : "Keep as note"}</button>
      </div>
      <ErrorNote error={error} />
    </Card>
  );
}
